import { RTEInitValue } from '@talentmesh/rte';
import * as yup from 'yup';
import type { AutocompleteOption } from '@/Components/shadcn';
import autoMapping from '@/Utils/autoMapping';
import { DefaultJobAd, JobAd } from '../../Models/JobAd';
import YupValidationErrors from '@/Utils/YupValidationErrors';
import AutocompleteOptionType from '@/Models/AutocompleteOptionType';
import UIStrings from '@/Utils/UIStrings';
import { AssessmentStatuses } from '@/Models/AssessmentData';

export const JobDescriptionCharactersLimit = 6000;

export enum JobAdFieldTypes {
    Title = 'title',
    HardSkills = 'hardSkills',
    HardSkillsSwitch = 'hardSkillsSwitch',
    SoftSkills = 'softSkills',
    SoftSkillsSwitch = 'softSkillsSwitch',
    Description = 'description',
    DescriptionLen = 'descriptionLen',
    CompanyDescription = 'companyDescription',
}

export interface JobAdFormValues {
    title: string;
    hardSkills: AutocompleteOption[];
    hardSkillsSwitch: boolean;
    softSkills: AutocompleteOption[];
    softSkillsSwitch: boolean;
    description: string;
    descriptionLen: number;
    companyDescription: string;
    status: AssessmentStatuses;
}

export const DefaultJobAdFormValues: JobAdFormValues = {
    title: '',
    hardSkills: [],
    hardSkillsSwitch: true,
    softSkills: [],
    softSkillsSwitch: true,
    description: RTEInitValue, // we use this initial value for correct RTE behavior is we add only list in description
    descriptionLen: 0,
    companyDescription: RTEInitValue,
    status: AssessmentStatuses.Active,
};

export function jobAdModel2FormValues(model: JobAd): JobAdFormValues {
    return autoMapping(model, DefaultJobAdFormValues);
}

export function jobAdFormValues2Model(values: JobAdFormValues): JobAd {
    return autoMapping(values, DefaultJobAd);
}

export function getAutocompleteSkillsType(type: AutocompleteOptionType): JobAdFieldTypes {
    switch (type) {
        case AutocompleteOptionType.HardSkill:
            return JobAdFieldTypes.HardSkills;
        case AutocompleteOptionType.SoftSkill:
            return JobAdFieldTypes.SoftSkills;
        default:
            throw new Error('invalid skills type');
    }
}

export function fillInInitialSkills(
    initialFormValues: JobAdFormValues,
    existingFormValues: JobAdFormValues,
): JobAdFormValues {
    // we always take skills from existing form values
    // because user can edit those during job ad generation
    return {
        ...initialFormValues,
        hardSkills: existingFormValues.hardSkills,
        softSkills: existingFormValues.softSkills,
        hardSkillsSwitch: existingFormValues.hardSkillsSwitch,
        softSkillsSwitch: existingFormValues.softSkillsSwitch,
    };
}

export function getSkillsSwitchName(type: AutocompleteOptionType): JobAdFieldTypes {
    switch (type) {
        case AutocompleteOptionType.HardSkill:
            return JobAdFieldTypes.HardSkillsSwitch;
        case AutocompleteOptionType.SoftSkill:
            return JobAdFieldTypes.SoftSkillsSwitch;
        default:
            throw new Error('invalid skills type');
    }
}

// @ts-expect-error because of incomplete schema
export const jobAdValidationSchema: yup.SchemaOf<JobAdFormValues> = yup.object().shape({
    title: yup
        .string()
        .required(YupValidationErrors.Required)
        .max(256, YupValidationErrors.JobAdTitleMaxSymbolsValidationError),
    hardSkillsSwitch: yup.boolean(),
    hardSkills: yup
        .array()
        .ensure()
        .when(getSkillsSwitchName(AutocompleteOptionType.HardSkill), {
            is: true,
            then: yup
                .array()
                .test(
                    'custom-validation',
                    YupValidationErrors.PleaseAddAtLeastOneSkillRequiredError('hard'),
                    (value) => {
                        return !!value && value.length > 0;
                    },
                ),
        }),
    softSkillsSwitch: yup.boolean(),
    softSkills: yup
        .array()
        .ensure()
        .when(getSkillsSwitchName(AutocompleteOptionType.SoftSkill), {
            is: true,
            then: yup
                .array()
                .test(
                    'custom-validation',
                    YupValidationErrors.PleaseAddAtLeastOneSkillRequiredError('soft'),
                    (value) => {
                        return !!value && value.length > 0;
                    },
                ),
        }),
    description: yup.string(),
    descriptionLen: yup
        .number()
        .min(1, YupValidationErrors.JobDescriptionRequiredError)
        .max(JobDescriptionCharactersLimit, YupValidationErrors.PleaseLimitDescriptionError),
    companyDescription: yup
        .string()
        .test('Required', UIStrings.Required, (value) => {
            const plainText = extractTextWithNewLinesFromHtml(value || '');
            return plainText.length !== 0;
        })
        .test('TooLong', UIStrings.CompanyDescriptionInputLengthValidation, (value) => {
            const plainText = extractTextWithNewLinesFromHtml(value || '');
            return plainText.length < 4000;
        }),
});

export function extractTextWithNewLinesFromHtml(html: string): string {
    const document = new DOMParser().parseFromString(html, 'text/html').documentElement;

    const elementsWithLineBreaks = ['p', 'h4', 'li'];
    elementsWithLineBreaks.forEach((elementName) => {
        const elements = document.getElementsByTagName(elementName);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.textContent = `${element.textContent}\n`;
        }
    });

    return document.textContent?.trim() || '';
}
